import { useState, useEffect, FC, useRef } from "react";
import "../CSS/CalendarStrip.css";
import { format, startOfMonth, endOfMonth, eachDayOfInterval } from "date-fns";

interface CalendarStripProps {
  selectedDate: Date;
  setSelectedDate: (date: string) => void;
  nextAvailableDate?: Date;
}

const CalendarStrip: FC<CalendarStripProps> = ({
  selectedDate,
  setSelectedDate,
  nextAvailableDate,
}) => {
  const [monthDays, setMonthDays] = useState<Date[]>([]);
  const selectedRef = useRef<HTMLDivElement | null>(null);
  const hasScrolledInitially = useRef<boolean>(false);

  useEffect(() => {
    // Get all days in the selected month
    const start = startOfMonth(selectedDate);
    const end = endOfMonth(selectedDate);
    const days = eachDayOfInterval({ start, end });
    setMonthDays(days);
  }, [selectedDate]);

  useEffect(() => {
    if (!hasScrolledInitially.current && selectedRef.current) {
      const parent = selectedRef.current.parentElement;
      if (parent && monthDays.length) {
        // Find index of selected date
        const selectedIndex = monthDays.findIndex(
          (d) => format(d, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd")
        );
        // If selected is one of last 4 days, scroll to right end
        if (selectedIndex >= monthDays.length - 4) {
          parent.scrollLeft = parent.scrollWidth - parent.clientWidth;
        } else {
          // Usual (left) scroll logic for all other days
          selectedRef.current.scrollIntoView({
            behavior: "auto",
            inline: "start",
            block: "nearest",
          });
          parent.scrollLeft =
            parent.scrollLeft - 24 > 0 ? parent.scrollLeft - 24 : 0;
        }
      }
      hasScrolledInitially.current = true;
    }
  }, [monthDays, selectedDate]);

  const handleSelect = (date: Date): void => {
    setSelectedDate(date.toISOString());
  };

  // ----- NEW LOGIC: current month from today; next month all days -----
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const selectedMonth = selectedDate.getMonth();
  const selectedYear = selectedDate.getFullYear();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();

  const isCurrentMonth =
    selectedMonth === currentMonth && selectedYear === currentYear;

  const isNextMonth =
    // same year, next month
    (selectedYear === currentYear && selectedMonth === currentMonth + 1) ||
    // December -> January of next year
    (currentMonth === 11 &&
      selectedYear === currentYear + 1 &&
      selectedMonth === 0);

  return (
    <div className="calendar-strip">
      {monthDays.map((date: Date) => {
        const isSelected =
          format(date, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd");

        const isNextAvailable =
          nextAvailableDate != null &&
          format(date, "yyyy-MM-dd") ===
            format(nextAvailableDate, "yyyy-MM-dd");

        // current month: only from today onwards
        // next month: all days
        // any other month: disabled
        const isSelectable = (isCurrentMonth && date >= today) || isNextMonth;

        return (
          <div
            key={date.toString()}
            className={`calendar-day ${isSelected ? "selected" : ""} ${
              !isSelectable ? "disabled" : ""
            } ${isNextAvailable && !isSelected ? "next-available" : ""}`}
            onClick={() => isSelectable && handleSelect(date)}
            ref={isSelected ? selectedRef : null}
            style={!isSelectable ? { pointerEvents: "none", opacity: 0.5 } : {}}
          >
            <div className="day-label">{format(date, "EEE").toUpperCase()}</div>
            <div className="day-number">{format(date, "d")}</div>
          </div>
        );
      })}
    </div>
  );
};

export default CalendarStrip;
